import React, { useCallback, useMemo, useRef, useState } from 'react'
import {
  Avatar,
  Button,
  Chip,
  CircularProgress,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core'
import { useMutation, useQueries, useQuery, useQueryClient } from 'react-query'
import { useTranslate } from 'react-admin'
import FacebookLogin from 'react-facebook-login/dist/facebook-login-render-props'
import QueryKeys from '../../../utils/constants/queryKeys'
import useQueryParams from '../../../hooks/useQueryParams'
import getCompetitions from '../../../Apis/competition/getCompetitions'
import getAccountStatus from '../../../Apis/social/getAccountStatus'
import getLongLivedToken from '../../../Apis/social/getLongLivedToken'
import getAccounts from '../../../Apis/social/getAccounts'
import postUnlinkSocials from '../../../Apis/social/unlinkSocials'
import LoaderDots from '../../../components/loaders/LoaderDots/LoaderDots'
import MetaAccountChoice from '../MetaAccountChoice/MetaAccountChoice'
import { FB_APP_ID, FB_SCOPE } from '../../../utils/constants/facebookLogin'

const useStyles = makeStyles((theme) => ({
  header: { marginBottom: theme.spacing(2) },
  tableContainer: { borderRadius: 8 },
  competitionInfo: { display: 'flex', alignItems: 'center', gap: 12 },
  logo: { width: 32, height: 32 },
  platformChips: { display: 'flex', gap: 6, marginTop: 4 },
  emptyState: { padding: theme.spacing(4), color: theme.palette.text.secondary },
  loader: { display: 'flex', justifyContent: 'center', padding: 48 },
}))

// Per-competition Meta account management (BUS-2799 / PNP-WEB-07, frontend-only subset).
// Federation-only: DistributionPage mounts this tab only for federations, which link a Meta
// account per competition. Clubs link their single org-level account on the Integrations page
// instead. Showing the linked account's display name and reusing an account across competitions
// are deferred until the backend exposes them (PNP-BE-13).
const SocialChannelsTab = () => {
  const classes = useStyles()
  const translate = useTranslate()
  const queryClient = useQueryClient()
  const { org: orgId } = useQueryParams()

  const { data: competitions, isLoading: competitionsLoading } = useQuery(
    [QueryKeys.GET_COMPETITIONS, orgId],
    () => getCompetitions(orgId),
    { enabled: Boolean(orgId) },
  )

  const rows = useMemo(
    () =>
      (competitions || []).map((c) => ({
        competitionId: c.id,
        name: c.name,
        logoUrl: c.logoUrl,
      })),
    [competitions],
  )

  // One connection-status query per competition row.
  const statusQueries = useQueries(
    rows.map((row) => ({
      queryKey: [QueryKeys.GET_SOCIAL_ACCOUNT_STATUS, orgId, row.competitionId],
      queryFn: () => getAccountStatus({ orgId, competitionId: row.competitionId }),
      enabled: Boolean(orgId),
    })),
  )

  const invalidateStatus = (competitionId) =>
    queryClient.invalidateQueries([QueryKeys.GET_SOCIAL_ACCOUNT_STATUS, orgId, competitionId])

  // --- Shared Meta connect flow ---
  const [metaAccounts, setMetaAccounts] = useState([])
  const [pendingCompetitionId, setPendingCompetitionId] = useState(null)
  const facebookInitiatedRef = useRef(false)

  const metaAccountsMutation = useMutation((token) => getAccounts(token), {
    onSuccess: (accounts) => setMetaAccounts(accounts),
  })

  const metaLongLivedToken = useMutation((accessToken) => getLongLivedToken({ orgId, accessToken }), {
    onSuccess: (longLived) => metaAccountsMutation.mutate(longLived),
  })

  const unlinkMutation = useMutation((competitionId) => postUnlinkSocials({ orgId, competitionId }), {
    onSuccess: (_res, competitionId) => invalidateStatus(competitionId),
  })

  // Guard against stray callbacks: only process a response we actually triggered.
  const metaCallback = useCallback(
    (res) => {
      if (!facebookInitiatedRef.current) return
      facebookInitiatedRef.current = false
      if (!res?.accessToken) return
      metaLongLivedToken.mutate(res.accessToken)
    },
    [metaLongLivedToken],
  )

  const handleFacebookClick = (onClick, competitionId) => {
    facebookInitiatedRef.current = true
    setPendingCompetitionId(competitionId)
    onClick()
  }

  const isConnecting = metaLongLivedToken.isLoading || metaAccountsMutation.isLoading

  if (competitionsLoading) {
    return (
      <div className={classes.loader}>
        <LoaderDots />
      </div>
    )
  }

  return (
    <div>
      <div className={classes.header}>
        <Typography variant='body2' color='textSecondary'>
          {translate('ra.text.socialChannelsHint')}
        </Typography>
      </div>

      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                {translate('ra.text.competition')}
              </TableCell>
              <TableCell align='right'>{translate('ra.text.metaAccount')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length > 0 ? (
              rows.map((row, index) => {
                const status = statusQueries[index]?.data
                const connected = Boolean(status?.connected)
                const rowBusy =
                  (unlinkMutation.isLoading && unlinkMutation.variables === row.competitionId) ||
                  (isConnecting && pendingCompetitionId === row.competitionId)
                return (
                  <TableRow key={row.competitionId || 'org'} hover>
                    <TableCell>
                      <div className={classes.competitionInfo}>
                        {row.logoUrl && (
                          <Avatar src={row.logoUrl} alt={row.name} className={classes.logo}>
                            {row.name?.charAt(0)}
                          </Avatar>
                        )}
                        <div>
                          <Typography variant='body2'>{row.name}</Typography>
                          {connected && (
                            <div className={classes.platformChips}>
                              {status?.facebookConnected && <Chip size='small' label='Facebook' />}
                              {status?.instagramConnected && <Chip size='small' label='Instagram' />}
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell align='right'>
                      {rowBusy ? (
                        <CircularProgress size={20} />
                      ) : connected ? (
                        <Button
                          color='secondary'
                          onClick={() => unlinkMutation.mutate(row.competitionId)}
                        >
                          {translate('ra.buttons.disconnect')}
                        </Button>
                      ) : (
                        <FacebookLogin
                          appId={FB_APP_ID}
                          autoLoad={false}
                          scope={FB_SCOPE}
                          fields='name,email,picture'
                          size='small'
                          callback={metaCallback}
                          render={({ onClick, isProcessing }) => (
                            <Button
                              size='small'
                              color='primary'
                              variant='outlined'
                              disabled={isProcessing || isConnecting}
                              onClick={() => handleFacebookClick(onClick, row.competitionId)}
                            >
                              {translate('ra.text.connect')}
                            </Button>
                          )}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                )
              })
            ) : (
              <TableRow>
                <TableCell colSpan={2} align='center' className={classes.emptyState}>
                  {translate('ra.text.noSocialCompetitions')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <MetaAccountChoice
        accounts={metaAccounts}
        setMetaAccounts={setMetaAccounts}
        orgId={orgId}
        competitionId={pendingCompetitionId}
        onConnected={() => invalidateStatus(pendingCompetitionId)}
      />
    </div>
  )
}

export default SocialChannelsTab
